/* Graph of Life brand v1.1.0 · tokens.json */
import React, { useEffect, useRef } from 'react';

// Ambient system-interface field. Two depth planes of nodes and edges,
// reactive to the cursor, morphing between an organic drift (ECOLOGICAL)
// and a structured lattice (FINANCIAL). Decorative, hidden from AT,
// and rendered as a single static frame under prefers-reduced-motion.
const InteractiveBackground = ({ viewMode = 'ECOLOGICAL', density = 1 }) => {
  const canvasRef = useRef(null);
  const modeRef = useRef(viewMode);
  const redrawRef = useRef(null);

  useEffect(() => {
    modeRef.current = viewMode;
    redrawRef.current?.();
  }, [viewMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = motionPreference.matches;

    let width = window.innerWidth;
    let height = window.innerHeight;
    let frameId;
    let structure = 0;
    let scrollFactor = 0;
    const mouse = { x: null, y: null };
    const planes = [];

    const PLANES = [
      { count: Math.round(52 * density), depth: 0.45, speed: 0.16, reach: 150 },
      { count: Math.round(30 * density), depth: 1, speed: 0.34, reach: 190 },
    ];

    const build = () => {
      planes.length = 0;
      PLANES.forEach((cfg) => {
        const cols = Math.ceil(Math.sqrt(cfg.count * (width / Math.max(height, 1))));
        const rows = Math.ceil(cfg.count / cols);
        const nodes = [];
        for (let i = 0; i < cfg.count; i += 1) {
          const gx = ((i % cols) + 0.5) * (width / cols);
          const gy = (Math.floor(i / cols) + 0.5) * (height / rows);
          nodes.push({
            ox: Math.random() * width,
            oy: Math.random() * height,
            gx,
            gy,
            x: 0,
            y: 0,
            vx: (Math.random() - 0.5) * cfg.speed,
            vy: (Math.random() - 0.5) * cfg.speed,
            r: 0.9 + Math.random() * 1.9 * cfg.depth,
            pulse: Math.random() * Math.PI * 2,
          });
        }
        planes.push({ cfg, nodes });
      });
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };

    const ease = (f) => (f < 0.5 ? 2 * f * f : 1 - (-2 * f + 2) ** 2 / 2);

    const render = (t) => {
      if (document.hidden) return;
      ctx.clearRect(0, 0, width, height);

      const target = modeRef.current === 'FINANCIAL' ? 1 : 0;
      structure += (target - structure) * (reduced ? 1 : 0.045);
      const f = ease(structure);

      // teal in structured mode, pale blue in organic mode
      const cr = 168 + (45 - 168) * f;
      const cg = 208 + (212 - 208) * f;
      const cb = 255 + (191 - 255) * f;

      planes.forEach(({ cfg, nodes }) => {
        const parallax = scrollFactor * 26 * cfg.depth;

        nodes.forEach((n) => {
          if (!reduced) {
            n.ox += n.vx;
            n.oy += n.vy;
            if (n.ox < 0) n.ox = width;
            if (n.ox > width) n.ox = 0;
            if (n.oy < 0) n.oy = height;
            if (n.oy > height) n.oy = 0;
            n.pulse += 0.012;
          }

          n.x = n.ox + (n.gx - n.ox) * f;
          n.y = n.oy + (n.gy - n.oy) * f - parallax;

          if (mouse.x != null) {
            const dx = mouse.x - n.x;
            const dy = mouse.y - n.y;
            const d = Math.hypot(dx, dy);
            if (d < 250) {
              const push = ((250 - d) / 250) * 0.075 * (1 - f * 0.6) * cfg.depth;
              n.x -= dx * push;
              n.y -= dy * push;
              n.ox -= dx * push * 0.6;
              n.oy -= dy * push * 0.6;
            }
          }
        });

        // edges, brighter close to the cursor
        ctx.lineWidth = 0.7 * cfg.depth;
        const reach = (cfg.reach + f * 46) * (reduced ? 1.8 : 1);
        for (let i = 0; i < nodes.length; i += 1) {
          for (let j = i + 1; j < nodes.length; j += 1) {
            const a = nodes[i];
            const b = nodes[j];
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            if (d > reach) continue;
            let alpha = (1 - d / reach) * (reduced ? 0.38 : 0.24) * cfg.depth;
            if (mouse.x != null) {
              const md = Math.hypot((a.x + b.x) / 2 - mouse.x, (a.y + b.y) / 2 - mouse.y);
              if (md < 285) alpha += (1 - md / 285) * 0.45 * cfg.depth;
            }
            ctx.strokeStyle = `rgba(${cr}, ${cg}, ${cb}, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }

        // nodes
        nodes.forEach((n) => {
          const breathe = reduced ? 1 : 0.75 + Math.sin(n.pulse) * 0.25;
          let alpha = ((reduced ? 0.5 : 0.32) + 0.45 * f) * cfg.depth * breathe;
          if (mouse.x != null) {
            const d = Math.hypot(n.x - mouse.x, n.y - mouse.y);
            if (d < 225) alpha += (1 - d / 225) * 0.7;
          }
          ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, ${Math.min(alpha, 0.98)})`;
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r * breathe * (reduced ? 1.15 : 1), 0, Math.PI * 2);
          ctx.fill();
        });
      });

      if (!reduced) frameId = requestAnimationFrame(render);
    };

    const onMove = (e) => {
      if (reduced) return;
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      scrollFactor = max > 0 ? window.scrollY / max : 0;
    };

    const onResize = () => { resize(); if (reduced) render(0); };
    const restart = () => { cancelAnimationFrame(frameId); render(0); };
    const onMotionChange = () => { reduced = motionPreference.matches; mouse.x = null; mouse.y = null; restart(); };
    redrawRef.current = () => { if (reduced) render(0); };
    resize();
    onScroll();
    render(0);

    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', restart);
    motionPreference.addEventListener('change', onMotionChange);

    return () => {
      redrawRef.current = null;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', restart);
      motionPreference.removeEventListener('change', onMotionChange);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      role="presentation"
      data-testid="ambient-field"
      className="fixed inset-0 z-0 pointer-events-none opacity-90"
    />
  );
};

export default InteractiveBackground;
